import React, { useState, useCallback } from 'react';
import { upperFirst, isFunction } from 'lodash';

/**
 * Custom hook for show/hide state management.
 * @param {string} name - The name prefix for state and handlers
 * @param {boolean|Function} [initialState=false] - Initial visibility state
 * @param {Object} [props] - Props to pass to initialState if it's a function
 * @returns {Object} Object with `${name}Showing`, `onShow${Name}`, `onHide${Name}`, `onToggle${Name}`
 */
export const useShowHide = (name, initialState = false, props = {}) => {
	const [showing, setShowing] = useState(
		isFunction(initialState) ? initialState(props) : initialState,
	);

	const onShow = useCallback(() => setShowing(true), []);
	const onHide = useCallback(() => setShowing(false), []);
	const onToggle = useCallback(() => setShowing((prev) => !prev), []);

	return {
		[`${name}Showing`]: showing,
		[`onShow${upperFirst(name)}`]: onShow,
		[`onHide${upperFirst(name)}`]: onHide,
		[`onToggle${upperFirst(name)}`]: onToggle,
	};
};

function withShowHide(name, initialState) {
	return (WrappedComponent) => {
		const WithShowHide = (props) => {
			const hookResult = useShowHide(name, initialState, props);
			return (
				<WrappedComponent
					{...props}
					{...hookResult}
				/>
			);
		};
		WithShowHide.displayName = `withShowHide(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
		return WithShowHide;
	};
}

export default withShowHide;
